import React, { ComponentType, ReactNode, useMemo, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { Checkbox, Empty, Input, Popover } from "antd5";
import classnames from "classnames";
import List from "rc-virtual-list";

import TextButton from "components/actions/TextButton";
import Button from "components/ui/button/Button";
import Badge from "lib/core_components/Badge";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { EventNames, useTracking } from "lib/tracking";

import css from "./MultiSelectDropdownList.module.scss";

/**
 * Caps every dropdown trigger. A title too wide for it truncates on one line rather than
 * stretching the filter row it sits in, or wrapping out of the button. Wide enough for the
 * longest label any current filter shows in full ("Assigned to anyone").
 *
 * Callers whose title is not plain text must also clamp it themselves — Button only applies
 * its own ellipsis when the children are a plain string.
 */
export const DROPDOWN_TRIGGER_MAX_WIDTH = 280;

export type Option<T> = {
  title: string;
  value: T;
  OptionComponent?: ReactNode;
};

type Props<T> = {
  title: string | ReactNode;
  fieldName?: string;
  description?: string;
  options: Option<T>[];
  selectedOptions: T[];
  onChange: (selectedOptions: T[]) => void;
  className?: string;
  overlayClassName?: string;
  optionClassName?: string;
  searchable?: boolean;
  OptionComponent?: ComponentType<{ title: string }>;
  disabled?: boolean;
  loading?: boolean;
  skipSort?: boolean;
  hideSelectAll?: boolean;
  trackingField?: "title" | "value"; // defaults to value
  virtualisation?: { height: number; itemHeight: number };
  showSelectedSizeBadge?: boolean;
  hideChevron?: boolean;
  /** Overrides {@link DROPDOWN_TRIGGER_MAX_WIDTH} for a trigger that needs a different width. */
  maxWidth?: number | string;
};

export function MultiSelectDropdown<T = string>({
  title,
  fieldName,
  description,
  onChange: onCheck,
  options,
  selectedOptions,
  optionClassName,
  className,
  overlayClassName,
  searchable,
  OptionComponent,
  disabled,
  hideSelectAll,
  loading,
  skipSort,
  trackingField = "value",
  virtualisation,
  showSelectedSizeBadge = true,
  hideChevron = false,
  maxWidth = DROPDOWN_TRIGGER_MAX_WIDTH,
}: Props<T>): JSX.Element {
  const { logEvent } = useTracking();
  const [searchTerm, setSearchTerm] = useState("");
  const filterTrackingEvent = React.useCallback(
    (field: T, selected: boolean) => {
      let filterName = fieldName;

      if (!filterName && typeof title === "string") {
        filterName = title;
      }

      logEvent(EventNames.filterActioned, {
        "Filter name": filterName,
        "Filter selection": field,
        "Action type": selected ? "Filter applied" : "Filter cleared",
      });
    },
    [logEvent, fieldName, title],
  );

  const selected = useMemo(() => new Set(selectedOptions), [selectedOptions]);

  const sortedOptions = useMemo(() => {
    const sorted = skipSort
      ? options
      : options.sort((a, b) =>
          a.title.trim().toLocaleLowerCase().localeCompare(b.title.trim().toLocaleLowerCase()),
        );

    if (!searchTerm) {
      return sorted;
    }

    return sorted.filter((s) =>
      s.title.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()),
    );
  }, [options, searchTerm, skipSort]);

  const overlay = React.useMemo(() => {
    const selectAll =
      selected.size === sortedOptions.length ? (
        <TextButton
          onClick={(e) => {
            e.stopPropagation();
            onCheck([]);
            filterTrackingEvent("All options" as unknown as T, false);
          }}
        >
          Deselect all
        </TextButton>
      ) : (
        <TextButton
          onClick={(e) => {
            e.stopPropagation();

            onCheck(options.map((o) => o.value));
            filterTrackingEvent("All options" as unknown as T, true);
          }}
        >
          Select all
        </TextButton>
      );

    if (sortedOptions.length === 0) {
      return (
        <div className={css.overlay} onClick={(e) => e.stopPropagation()}>
          {searchable && (
            <div className={css.inputContainer}>
              <Input
                autoFocus
                prefix={<SearchOutlined />}
                placeholder="Search..."
                className={css.input}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
              />
            </div>
          )}
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      );
    } else {
      const renderOption = (option: Option<T>) => (
        <li key={`${option.value}`} className={css.listItem} onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={selected.has(option.value)}
            className={css.optionWrapper}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onChange={(e) => {
              e.stopPropagation();
              onCheck(selected.delete(option.value) ? [...selected] : [...selected, option.value]);
              filterTrackingEvent(option[trackingField] as T, e.target.checked);
            }}
          >
            <span className={optionClassName} onClick={(e) => e.stopPropagation()}>
              {option.OptionComponent ? (
                option.OptionComponent
              ) : OptionComponent ? (
                <OptionComponent title={option.title} />
              ) : (
                option.title
              )}
            </span>
          </Checkbox>
        </li>
      );
      return (
        <div className={classnames(css.overlay, overlayClassName)} aria-label={`${fieldName} menu`}>
          {(description || !hideSelectAll || searchable) && (
            <div className={css.selectAll} onClick={(e) => e.stopPropagation()}>
              {description && <p className={css.description}>{description}</p>}
              {!hideSelectAll && selectAll}
              {searchable && (
                <Input
                  autoFocus
                  prefix={<SearchOutlined />}
                  placeholder="Search..."
                  className={css.input}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  allowClear
                />
              )}
            </div>
          )}
          <ul className={css.list}>
            {virtualisation ? (
              <List
                data={sortedOptions}
                height={virtualisation.height}
                itemHeight={virtualisation.itemHeight}
                virtual
                itemKey="value"
              >
                {renderOption}
              </List>
            ) : (
              sortedOptions.map((o) => renderOption(o))
            )}
          </ul>
        </div>
      );
    }
  }, [
    selected,
    sortedOptions,
    onCheck,
    filterTrackingEvent,
    options,
    searchable,
    searchTerm,
    overlayClassName,
    fieldName,
    description,
    hideSelectAll,
    virtualisation,
    optionClassName,
    OptionComponent,
    trackingField,
  ]);

  return (
    <Popover
      trigger={["click"]}
      content={overlay}
      arrow={false}
      placement="bottomLeft"
      overlayInnerStyle={{ padding: 0 }}
    >
      <Button
        buttonType="secondary"
        className={className}
        loading={loading}
        disabled={disabled}
        maxWidth={maxWidth}
        aria-label={fieldName}
      >
        {title}
        {showSelectedSizeBadge && !!selected.size && <Badge>{selected.size}</Badge>}
        {!hideChevron && <UIcon icon="chevronDown" size={16} />}
      </Button>
    </Popover>
  );
}

export default MultiSelectDropdown;
